import type { CreatorSubscriptionProvider } from '../types/global/creatorSubscription.types';

export const CREATOR_SUBSCRIPTION_MANAGE_COPY = {
    /** Opens in-app add/select card modal (hosted provider update is a secondary option inside that modal). */
    paystackUpdatePaymentButton: 'Update payment method',
    flutterwaveUpdatePaymentButton: 'Update payment method',
    stripeUpdatePaymentButton: 'Update payment method',
    paystackCancelConfirm:
        'Your plan stays active until the end of the current billing period. You can reactivate before then if your plan is still eligible.',
    flutterwaveCancelConfirm:
        'Your plan stays active until the end of the current billing period. You can reactivate before then if your plan is still eligible.',
    stripeCancelConfirm:
        'Your plan stays active until the end of the current billing period. After that you will need to subscribe again to restore benefits.',
    cancelPremiumCampaignWarning:
        'You will keep access until your billing period ends. After that you may be removed from these premium campaigns:',
    downgradePremiumCampaignWarning:
        'When the lower plan takes effect, you may be removed from these approved premium campaigns that require a higher plan:',
    reactivateButton: 'Reactivate subscription',
    reactivateSuccess: 'Subscription reactivated.',
    reactivateFail: 'Could not reactivate subscription. Please try again.',
    reactivateUnavailable:
        'This subscription can no longer be reactivated. Opening checkout so you can subscribe again.',
    resubscribeRedirectFail: 'Could not start checkout to subscribe again. Please pick a plan below.',
    canceledStripEyebrowActive: 'Canceled plan',
    canceledStripEyebrowEnded: 'Previous plan',
    canceledStripCta: 'Subscribe again',
} as const;

export function getCreatorCanceledStripSubtitle(periodEndIso: string | null | undefined, formattedEnd: string): string {
    if (!periodEndIso || !formattedEnd || formattedEnd === '—') {
        return 'Subscribe again to restore benefits.';
    }
    const endMs = Date.parse(periodEndIso);
    if (Number.isFinite(endMs) && endMs > Date.now()) {
        return `Access until ${formattedEnd}. Subscribe again to keep your benefits.`;
    }
    return `Ended ${formattedEnd}. Subscribe again to restore benefits.`;
}

export function getCreatorUpdatePaymentButtonLabel(provider: CreatorSubscriptionProvider): string {
    if (provider === 'paystack') return CREATOR_SUBSCRIPTION_MANAGE_COPY.paystackUpdatePaymentButton;
    if (provider === 'flutterwave') return CREATOR_SUBSCRIPTION_MANAGE_COPY.flutterwaveUpdatePaymentButton;
    if (provider === 'stripe') return CREATOR_SUBSCRIPTION_MANAGE_COPY.stripeUpdatePaymentButton;
    return 'Manage billing';
}

export function getCreatorCancelConfirmCopy(provider: CreatorSubscriptionProvider): string {
    if (provider === 'paystack') return CREATOR_SUBSCRIPTION_MANAGE_COPY.paystackCancelConfirm;
    if (provider === 'flutterwave') return CREATOR_SUBSCRIPTION_MANAGE_COPY.flutterwaveCancelConfirm;
    if (provider === 'stripe') return CREATOR_SUBSCRIPTION_MANAGE_COPY.stripeCancelConfirm;
    return 'Your plan will remain active until the end of the current billing period. Are you sure you want to cancel?';
}

/** Lead sentence for cancel confirm when approved premium campaigns are at risk. */
export function getCreatorCancelPremiumImpactLead(campaignTitles: string[]): string | null {
    const titles = campaignTitles.map(t => String(t || '').trim()).filter(Boolean);
    if (!titles.length) return null;
    if (titles.length === 1) {
        return `Canceling your subscription may lead to your removal from “${titles[0]}”.`;
    }
    return `Canceling your subscription may lead to your removal from ${titles.length} premium campaigns.`;
}

export function formatPremiumCampaignImpactBody(intro: string, campaignTitles: string[], baseCopy: string): string {
    if (!campaignTitles.length) return baseCopy;
    const list = campaignTitles.map(t => `• ${t}`).join('\n');
    return `${baseCopy}\n\n${intro}\n${list}`;
}
